import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor";

const BASE_URL = "https://api.kennapartners.com/api/v1";

const Sustainability = () => {
  const [sustainabilityRecords, setSustainabilityRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  
  // Add effect to log filteredRecords changes
  useEffect(() => {
    console.log("📊 FILTEREDRECORDS STATE CHANGED:", filteredRecords.length, "records");
    if (filteredRecords.length > 0) {
      console.log("📊 First 3 titles:", filteredRecords.slice(0, 3).map(r => r.title));
    }
  }, [filteredRecords]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false); // Added for details modal
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(""); // Add this line
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState("");
const [fileName, setFileName] = useState(''); // Track file name separately

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching

  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null); // Added to store selected record details
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // You can make this configurable if needed
  const [totalItems, setTotalItems] = useState(0);

  const [formData, setFormData] = useState({
    title: "",
    content: {
      html: "",
      text: "",
    },
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSustainabilityRecords();
  }, [currentPage]); // Re-fetch records when page changes

  // Initialize filteredRecords ONLY on initial load
  useEffect(() => {
    console.log("🔄 sustainabilityRecords changed. Length:", sustainabilityRecords.length, "isInitialLoad:", isInitialLoadRef.current, "isSearching:", isSearchingRef.current);
    if (sustainabilityRecords.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
      console.log("✨ INITIAL load - setting filteredRecords");
      setFilteredRecords(sustainabilityRecords);
      isInitialLoadRef.current = false; // Mark that initial load is done
    }
  }, [sustainabilityRecords]);

  // Search functionality
  useEffect(() => {
    console.log("🔄 Search useEffect triggered. searchTerm:", `"${searchTerm}"`);
    if (searchTerm.trim()) {
      isSearchingRef.current = true;
      const debounceTimer = setTimeout(() => {
        console.log("⏰ Debounce complete, calling searchAllRecords");
        searchAllRecords(searchTerm.trim());
      }, 300);
      return () => {
        console.log("🧹 Cleaning up debounce timer");
        clearTimeout(debounceTimer);
      };
    } else {
      // Search cleared - reset to current page
      isSearchingRef.current = false;
      console.log("📋 No search term, resetting to current page records:", sustainabilityRecords.length);
      setFilteredRecords(sustainabilityRecords);
    }
  }, [searchTerm]);

  const searchAllRecords = async (term) => {
    console.log("🔍 SEARCH TRIGGERED with term:", term);
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/sustainability/?page=1&limit=10000`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      console.log("📦 Raw API response:", data);
      
      const allRecords = data.data?.sustainability || data.sustainability || [];
      console.log("📊 Total records fetched:", allRecords.length);
      console.log("📝 First record sample:", allRecords[0]);

      const filtered = allRecords.filter(
        (record) => {
          const titleMatch = record?.title?.toLowerCase().startsWith(term.toLowerCase());
          
          if (titleMatch) {
            console.log("✅ MATCH FOUND:", {
              title: record?.title,
              titleMatch
            });
          }
          
          return titleMatch; // ONLY return titles that START WITH the search term
        }
      );

      console.log("🎯 Filtered results count:", filtered.length);
      console.log("🎯 Filtered results:", filtered);
      console.log("✅ About to setFilteredRecords with", filtered.length, "items. isSearching:", isSearchingRef.current);
      setFilteredRecords(filtered);
      console.log("✅ setFilteredRecords complete");
    } catch (error) {
      console.error("❌ Error searching sustainability records:", error);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSustainabilityRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/sustainability/?page=${currentPage}&limit=${itemsPerPage}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      const records = data.data?.sustainability || data.sustainability || data;
      if (!Array.isArray(records)) {
        throw new Error("Invalid data format received from API");
      }

      setSustainabilityRecords(records);
      setFilteredRecords(records); // Initialize filtered records with fetched records
      setTotalItems(data.data?.total_sustainability || 0); // Assuming API returns total_sustainability
      setError("");
    } catch (error) {
      console.error("Error fetching sustainability records:", error);
      setError("Failed to fetch sustainability records. Please try again.");
      setSustainabilityRecords([]);
      setFilteredRecords([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };


  // Update file handler
const handleFileChange = (e) => {
  const selectedFile = e.target.files[0];
  if (!selectedFile) { setFile(null); setFileName(""); return; }
  if (selectedFile.size > 2097152) {
    setFileSizeError(`⚠️ "${selectedFile.name}" is ${(selectedFile.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`);
    e.target.value = "";
    return;
  }
  setFileSizeError("");
  setFile(selectedFile);
  setFileName(selectedFile.name);
  setError("");
};


  // Update the file upload handler


// Updated handleFileUpload function with proper token handling
const handleFileUpload = async (recordId) => {
  if (!file) {
    console.log("No file selected for upload");
    return null;
  }

  try {
    const token = localStorage.getItem("access_token");
    if (!token) {
      throw new Error("Authentication token not found");
    }

    // Ensure the token is properly formatted (remove any extra quotes or spaces)
    const cleanToken = token.replace(/^["']|["']$/g, '').trim();

    // Use a different variable name to avoid confusion with component state
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    // Debug logging
    console.log("File upload details:", {
      recordId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      endpoint: `${BASE_URL}/sustainability/${recordId}/upload`,
      tokenLength: cleanToken.length
    });

    // Log FormData contents (for debugging)
    for (let pair of uploadFormData.entries()) {
      console.log("FormData entry:", pair[0], pair[1]);
    }

    const response = await fetch(
      `${BASE_URL}/sustainability/${recordId}/upload`,
      {
        method: "PATCH",
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          // Don't set Content-Type header when using FormData - let browser set it
        },
        body: uploadFormData,
      }
    );

    console.log("Upload response status:", response.status);
    console.log("Upload response headers:", Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      let errorData;
      const contentType = response.headers.get('content-type');
      
      try {
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const textResponse = await response.text();
          console.log("Non-JSON error response:", textResponse);
          errorData = { message: textResponse || `HTTP ${response.status}` };
        }
      } catch (parseError) {
        console.error("Error parsing response:", parseError);
        errorData = { message: `HTTP ${response.status} - Unable to parse response` };
      }

      console.error("Upload error response:", errorData);
      
      // Handle specific error types
      if (response.status === 401) {
        throw new Error("Authentication failed. Please login again.");
      } else if (response.status === 422) {
        const validationError = errorData?.detail?.[0]?.msg || errorData?.message || "Validation error";
        throw new Error(`Validation error: ${validationError}`);
      }
      
      throw new Error(
        typeof errorData === 'object' 
          ? (errorData.message || errorData.detail || JSON.stringify(errorData))
          : errorData
      );
    }

    const result = await response.json();
    console.log("File upload successful:", result);
    return result;

  } catch (error) {
    console.error("Error in file upload:", {
      message: error.message,
      fileName: file?.name,
      fileType: file?.type,
      fileSize: file?.size,
      stack: error.stack
    });
    throw new Error(error.message || "File upload failed");
  }
};

  const fetchSustainabilityRecordDetails = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/sustainability/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      const recordDetails =
        data.data?.sustainability || data.sustainability || data;
      setSelectedRecordDetails(recordDetails);
      setError("");
    } catch (error) {
      console.error("Error fetching sustainability record details:", error);
      setError(
        "Failed to fetch sustainability record details. Please try again."
      );
      setSelectedRecordDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

const resetForm = () => {
  setFormData({
    title: "",
    content: {
      html: "",
      text: "",
    },
    date: new Date().toISOString().split("T")[0],
  });
  setFile(null);
  setFileName(''); // Add this line
  setError(""); // Clear errors when resetting
};

// Update the handleCreateRecord function
const handleCreateRecord = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Process any blob images in the content (from RichTextEditor)
      const processedHtml = await processContentImages(
        formData.content.html,
        token
      );
      const processedText = processedHtml.replace(/<[^>]*>/g, "");

      // Prepare payload for creating the record (without the file)
      const payload = {
        title: formData.title,
        content: {
          html: processedHtml,
          text: processedText,
        },
        date: new Date(formData.date).toISOString(),
      };

      console.log("Creating record with payload (excluding file):", payload);

      // STEP 1: Submit the main record data
      const response = await fetch(`${BASE_URL}/sustainability/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response from record creation:", errorData);
        throw new Error(
          errorData.message ||
            errorData.detail ||
            `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      const newRecord = data.data?.sustainability || data.sustainability || data;
      const newRecordId = newRecord?.id;

      if (!newRecordId) {
        throw new Error("Failed to get record ID from server response");
      }

      // STEP 2: Now, upload the main file if it exists, using the new record ID
      if (file) {
        try {
          await uploadFile(newRecordId, file, token);
        } catch (uploadError) {
          // Log the file upload error but don't prevent record creation success
          console.error("Main file upload failed after record creation:", uploadError);
          setError(
            `Record created, but file upload failed: ${uploadError.message}`
          );
        }
      }

      setShowCreateModal(false);
      resetForm();
      fetchSustainabilityRecords(); // Refresh the list
      setSuccessMessage("Sustainability record created successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error creating sustainability record:", error);
      setError(
        error.message ||
          "Failed to create sustainability record. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

// Update the handleUpdateRecord function
const handleUpdateRecord = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!selectedRecordId) {
      setError("No record selected for update");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Process any blob images in the content (from RichTextEditor)
      const processedHtml = await processContentImages(
        formData.content.html,
        token
      );
      const processedText = processedHtml.replace(/<[^>]*>/g, "");

      // Prepare payload for updating the record (without the file)
      const payload = {
        title: formData.title,
        content: {
          html: processedHtml,
          text: processedText,
        },
        date: new Date(formData.date).toISOString(),
      };

      console.log("Updating record with payload (excluding file):", payload);

      // Optimistic UI update (optional, but good for responsiveness)
      setSustainabilityRecords((prev) =>
        prev.map((record) =>
          record.id === selectedRecordId
            ? { ...record, ...payload, content: {html: processedHtml, text: processedText}}
            : record
        )
      );

      // STEP 1: Submit the main record data update
      const response = await fetch(
        `${BASE_URL}/sustainability/${selectedRecordId}`,
        {
          method: "PUT", // Use PUT for full replacement, PATCH if only partial update is allowed
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response from record update:", errorData);
        throw new Error(
          errorData.message ||
            errorData.detail ||
            `HTTP error! status: ${response.status}`
        );
      }

      // STEP 2: Now, upload the main file if a new one was selected
      if (file) {
        try {
          await uploadFile(selectedRecordId, file, token);
        } catch (uploadError) {
          // Log the file upload error but don't prevent record update success
          console.error("Main file upload failed after record update:", uploadError);
          setError(
            `Record updated, but file upload failed: ${uploadError.message}`
          );
        }
      }

      setShowEditModal(false);
      resetForm();
      fetchSustainabilityRecords(); // Refresh the list to ensure consistency
      setSuccessMessage("Sustainability record updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating sustainability record:", error);
      setError(
        error.message ||
          "Failed to update sustainability record. Please try again."
      );
      // Revert optimistic update if error occurs
      fetchSustainabilityRecords(); // Re-fetch to get actual state
    } finally {
      setLoading(false);
    }
  };

  const processContentImages = async (htmlContent, token) => {
    // Extract all blob image URLs from content
    const imageUrls = [];
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    let match;

    while ((match = imgRegex.exec(htmlContent)) !== null) {
      if (match[1].startsWith("blob:")) {
        imageUrls.push(match[1]);
      }
    }

    // Upload images
    const uploadedImageUrls = await Promise.all(
      imageUrls.map(async (tempUrl) => {
        try {
          const response = await fetch(tempUrl);
          const blob = await response.blob();
          const file = new File([blob], `image-${Date.now()}.png`, {
            type: blob.type,
          });

          const formData = new FormData();
          formData.append("file", file);

          const uploadResponse = await fetch(
            `${BASE_URL}/sustainability/editor/upload`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            }
          );

          const responseData = await uploadResponse.json();
          const imageUrl = responseData?.data?.file_url;
          if (!imageUrl) {
            throw new Error("Server response missing image URL");
          }

          return { tempUrl, permUrl: imageUrl };
        } catch (error) {
          console.error(`Error uploading image (${tempUrl}):`, error);
          return { tempUrl, permUrl: null, error: error.message };
        }
      })
    );

    // Replace blob URLs with permanent URLs
    let cleanedContent = htmlContent;
    const failedUploads = [];

    uploadedImageUrls.forEach(({ tempUrl, permUrl, error }) => {
      if (permUrl) {
        cleanedContent = cleanedContent.replace(
          new RegExp(escapeRegExp(tempUrl), "g"),
          permUrl
        );
      } else {
        failedUploads.push({ tempUrl, error });
      }
    });

    // Remove any failed blob images
    cleanedContent = cleanedContent.replace(
      /<img[^>]*src="blob:[^"]*"[^>]*>/g,
      ""
    );

    if (failedUploads.length > 0) {
      console.warn(
        `${failedUploads.length} images failed to upload:`,
        failedUploads
      );
      setError(
        `Warning: ${failedUploads.length} images could not be uploaded and were removed`
      );
    }

    return cleanedContent;
  };
// Helper function to upload a file
  const uploadFile = async (recordId, fileToUpload, token) => {
    if (!fileToUpload) {
      console.log("No file to upload for record ID:", recordId);
      return null;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", fileToUpload);

      const uploadResponse = await fetch(
        `${BASE_URL}/sustainability/${recordId}/upload`,
        {
          method: "PATCH", // Or POST, depending on your API. PATCH is common for updating a resource's part.
          headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set Content-Type here; browser sets it automatically for FormData
          },
          body: uploadFormData,
        }
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        console.error("File upload failed response:", errorData);
        throw new Error(
          errorData.message ||
            errorData.detail ||
            `File upload failed! status: ${uploadResponse.status}`
        );
      }

      const uploadResult = await uploadResponse.json();
      console.log("File upload successful:", uploadResult);
      return uploadResult;
    } catch (uploadError) {
      console.error(`Error uploading file for record ${recordId}:`, uploadError);
      throw uploadError; // Re-throw to be caught by the main handler
    }
  };

  // Helper function to escape strings for regex
  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  const handleDeleteRecord = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/sustainability/${selectedRecordId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSustainabilityRecords((prev) =>
        prev.filter((record) => record.id !== selectedRecordId)
      );
      setShowDeleteModal(false);
      setSelectedRecordId(null);
      setError("");
      // Reset to page 1 if we deleted the last item on the current page
      if (filteredRecords.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting sustainability record:", error);
      setError("Failed to delete sustainability record. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (record) => {
    setFormData({
      title: record.title || "",
      content: {
        html: record.content?.html || record.content || "",
        text:
          record.content?.text ||
          (typeof record.content === "string" ? record.content : "") ||
          "",
      },
      date: record.date
        ? record.date.split("T")[0]
        : new Date().toISOString().split("T")[0],
    });
    setSelectedRecordId(record.id);
    setShowEditModal(true);
  };

  const openDeleteModal = (recordId) => {
    setSelectedRecordId(recordId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (recordId) => {
    setLoading(true);
    setShowDetailsModal(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/sustainability/${recordId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const recordDetails =
        data.data?.sustainability || data.sustainability || data;

      setSelectedRecordDetails({
        ...recordDetails,
        // Ensure content is always in the correct format
        content:
          typeof recordDetails.content === "string"
            ? recordDetails.content
            : recordDetails.content?.html || "",
      });
      setError("");
    } catch (error) {
      console.error("Error fetching sustainability record details:", error);
      setError("Failed to load sustainability record details");
      setSelectedRecordDetails(null);
    } finally {
      setLoading(false);
    }
  };
  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setSelectedRecordId(null);
    resetForm();
  };

  // Pagination functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-between items-center mt-4 p-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> records
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page
                  ? ""
                  : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="workspace">
      {fileSizeError && (
        <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",alignItems:"flex-start",gap:"12px",backgroundColor:"#dc2626",color:"white",fontSize:"14px",fontWeight:"500",padding:"16px 20px",borderRadius:"10px",boxShadow:"0 10px 40px rgba(0,0,0,0.35)",maxWidth:"500px",width:"calc(100% - 32px)"}}>
          <span style={{fontSize:"20px",lineHeight:"1",flexShrink:0}}>🚫</span>
          <div style={{flex:1,lineHeight:"1.5"}}>{fileSizeError}</div>
          <button onClick={()=>setFileSizeError("")} style={{marginLeft:"8px",background:"none",border:"none",color:"white",fontSize:"22px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      )}
      <Breadcrumb title="Sustainability">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Sustainability</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button
            className="float-right text-white"
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Sustainability Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Sustainability Record
          </button>
          <button
            onClick={fetchSustainabilityRecords}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search sustainability records by title or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !sustainabilityRecords?.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading sustainability records...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/3">Title</th>
                      <th className="w-1/3">Publication Date</th>
                      <th className="w-1/3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!filteredRecords || filteredRecords.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-8">
                          No sustainability records found. Create your first
                          record!
                        </td>
                      </tr>
                    ) : (
                      filteredRecords.map((record) => (
                        <tr key={record?.id}>
                          <td className="w-1/3">
                            <div className="font-medium">
                              {record?.title || "No title"}
                            </div>
                          </td>
                          <td className="w-1/3">
                            {record?.date
                              ? new Date(record.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                              : "-"}
                          </td>
                          <td className="w-1/3">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openEditModal(record)}
                                className="btn btn-sm btn-warning"
                                title="Edit Record"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                onClick={() => openDetailsModal(record?.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(record?.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Record"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()} {/* Render pagination controls */}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Sustainability Record
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleCreateRecord}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Content *
                      </label>
                      <RichTextEditor
                        value={formData.content.html}
                        onChange={(html) =>
                          setFormData((prev) => ({
                            ...prev,
                            content: {
                              html: html,
                              text: html.replace(/<[^>]*>/g, ""), // Strip HTML tags for text version
                            },
                          }))
                        }
                        uploadEndpoint={`${BASE_URL}/sustainability/editor/upload`}
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Date *
                      </label>
                      <Input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

<div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Upload File
  </label>
  <input
    type="file"
    onChange={handleFileChange}
    className="block w-full text-sm text-gray-600
        file:mr-4 file:py-2 file:px-4
        file:rounded-md file:border-0
        file:text-sm file:font-medium
        file:bg-blue-50 file:text-blue-700
        hover:file:bg-blue-100"
    accept=".jpg,.jpeg,.png"  // Specific formats as requested
  />
  {file && (
    <p className="text-sm text-gray-500 mt-1">
      Selected: {fileName}
    </p>
  )}
  <p className="text-xs text-gray-500 mt-1">
    Supported formats: .jpg, .png, .jpeg
  </p>
</div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit Sustainability Record
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleUpdateRecord}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Content *
                      </label>
                      <RichTextEditor
                        value={formData.content.html}
                        onChange={(html) =>
                          setFormData((prev) => ({
                            ...prev,
                            content: {
                              html: html,
                              text: html.replace(/<[^>]*>/g, ""), // Strip HTML tags for text version
                            },
                          }))
                        }
                        uploadEndpoint={`${BASE_URL}/sustainability/editor/upload`}
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Date *
                      </label>
                      <Input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                <div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Upload File
  </label>
  <input
    type="file"
    onChange={handleFileChange}
    className="block w-full text-sm text-gray-600
        file:mr-4 file:py-2 file:px-4
        file:rounded-md file:border-0
        file:text-sm file:font-medium
        file:bg-blue-50 file:text-blue-700
        hover:file:bg-blue-100"
    accept=".jpg,.jpeg,.png"  // Specific formats as requested
  />
  {file && (
    <p className="text-sm text-gray-500 mt-1">
      Selected: {fileName}
    </p>
  )}
  <p className="text-xs text-gray-500 mt-1">
    Supported formats: .jpg, .png, .jpeg
  </p>
</div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b space-x-4">
                    <Button
                      type="button"
                      onClick={closeAllModals}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Close
                    </Button>
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-whit e"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        "Update Record"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete this sustainability record?
                    This action cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeleteRecord}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete Record"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="text-xl font-semibold text-gray-800">
                    Sustainability Record Details
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-4">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p>Loading details...</p>
                    </div>
                  ) : selectedRecordDetails ? (
                    <div className="space-y-6">
                      {/* Title Section */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Title
                          </h3>
                        </div>
                        <div className="col-span-3">
                          <p className="text-gray-900 font-medium">
                            {selectedRecordDetails.title || "Not specified"}
                          </p>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Content
                          </h3>
                        </div>
                        <div className="col-span-3">
                          <div
                            className="prose max-w-none overflow-y-auto max-h-[50vh]
                prose-headings:text-gray-800
                prose-p:text-gray-700 prose-p:my-2
                prose-ul:list-disc prose-ul:pl-5 prose-ul:my-2
                prose-ol:list-decimal prose-ol:pl-5 prose-ol:my-2
                prose-li:my-1 prose-li:pl-1
                prose-strong:font-semibold
                prose-em:italic
                prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
                prose-img:rounded-lg prose-img:shadow-md prose-img:my-3"
                            dangerouslySetInnerHTML={{
                              __html:
                                selectedRecordDetails.content?.html ||
                                selectedRecordDetails.content ||
                                "No content available",
                            }}
                          />
                        </div>
                      </div>

                      {/* Date Section */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Publication Date
                          </h3>
                        </div>
                        <div className="col-span-3">
                          <p className="text-gray-900">
                            {selectedRecordDetails.date
                              ? new Date(
                                  selectedRecordDetails.date
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "Not specified"}
                          </p>
                        </div>
                      </div>

                      {/* File Section */}
                      {selectedRecordDetails.file_url && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                          <div className="col-span-1">
                            <h3 className="text-sm font-medium text-gray-500">
                              Attachment
                            </h3>
                          </div>
                          <div className="col-span-3">
                            <a
                              href={selectedRecordDetails.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <span className="la la-file-pdf text-lg mr-2"></span>
                              View File
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Metadata Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-gray-500 mb-2">
                            Created
                          </h3>
                          <p className="text-gray-900">
                            {selectedRecordDetails.created_at
                              ? new Date(
                                  selectedRecordDetails.created_at
                                ).toLocaleString()
                              : "Not available"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-gray-500 mb-2">
                            Last Updated
                          </h3>
                          <p className="text-gray-900">
                            {selectedRecordDetails.updated_at
                              ? new Date(
                                  selectedRecordDetails.updated_at
                                ).toLocaleString()
                              : "Not available"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-500 mb-4">
                        Failed to load sustainability record details.
                      </p>
                      <button
                        onClick={() =>
                          fetchSustainabilityRecordDetails(selectedRecordId)
                        }
                        className="btn bg-blue-500 text-white px-4 py-2 rounded"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>

                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={closeAllModals}
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md shadow-sm"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Sustainability;